/**
 * Tooltip Component
 * A customizable tooltip component with dynamic sizing and positioning
 * Based on the Skyporter tooltip design
 *
 * Supports both direct messages and i18n translation keys
 * Supports both hover and click trigger modes
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, ClickAwayListener } from '@mui/material';
import { useTranslation } from '../../../hooks/useTranslation';
import { colors } from '../../../styles/theme/colors';

export interface TooltipProps {
  children: React.ReactElement;
  message: string;
  wrapperSx?: object;
  /** Optional translation parameters for interpolation (e.g., {name: "John"}) */
  messageParams?: Record<string, string | number>;
  /** If true, treats message as a translation key. If false, treats as literal text. Default: auto-detect */
  useTranslation?: boolean;
  width?: number | string;
  maxWidth?: number | string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  backgroundColor?: string;
  textColor?: string;
  arrow?: boolean;
  /** Delay before showing tooltip (only for hover mode) */
  delay?: number;
  disabled?: boolean;
  /** Trigger mode: 'hover' or 'click'. Default: 'hover' */
  trigger?: 'hover' | 'click';
  /** Controlled open state (optional, for click mode) */
  open?: boolean;
  /** Callback when tooltip should close (for click mode) */
  onClose?: () => void;
  /** Callback when tooltip should open (for click mode) */
  onOpen?: () => void;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  message,
  messageParams,
  useTranslation: useTranslationProp,
  width = 'auto',
  maxWidth = 187,
  placement = 'top',
  backgroundColor = colors.tooltip,
  textColor = '#FFFFFF',
  arrow = true,
  delay = 200,
  disabled = false,
  trigger = 'hover',
  open: controlledOpen,
  onClose,
  onOpen,
  wrapperSx,
}) => {
  const { t } = useTranslation();
  const [internalVisible, setInternalVisible] = useState(false);
  // null = not positioned yet (measuring phase), object = ready to show
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Auto-detect if message is a translation key (contains dots like "tooltips.categoryMismatch")
  const shouldTranslate = useTranslationProp ?? message.includes('.');

  // Get the display message - either translate it or use as-is
  const displayMessage = shouldTranslate ? t(message, messageParams) : message;

  // Determine visibility based on trigger mode
  const isVisible = trigger === 'click' && controlledOpen !== undefined
    ? controlledOpen
    : internalVisible;

  const handleMouseEnter = () => {
    if (disabled || trigger === 'click') return;
    timeoutRef.current = setTimeout(() => {
      setInternalVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (trigger === 'click') return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setInternalVisible(false);
    setTooltipPosition(null);
  };

  const handleClick = () => {
    if (disabled || trigger === 'hover') return;

    if (controlledOpen !== undefined) {
      if (isVisible && onClose) onClose();
      else if (!isVisible && onOpen) onOpen();
    } else {
      setInternalVisible(!internalVisible);
    }
  };

  const handleClickAway = () => {
    if (disabled || trigger === 'hover') return;

    if (controlledOpen !== undefined && onClose) {
      onClose();
    } else {
      setInternalVisible(false);
      setTooltipPosition(null);
    }
  };

  // Two-phase positioning: tooltip is portalled to body and rendered off-screen first,
  // then rAF measures it and snaps it into the correct position relative to the trigger.
  useEffect(() => {
    if (!isVisible) {
      setTooltipPosition(null);
      return;
    }

    const frameId = requestAnimationFrame(() => {
      if (!triggerRef.current || !tooltipRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;

      const gap = arrow ? 10 : 8;
      let top = 0;
      let left = 0;

      switch (placement) {
        case 'top':
          top = triggerRect.top - tooltipHeight - gap;
          left = triggerRect.left + (triggerRect.width - tooltipWidth) / 2;
          break;
        case 'bottom':
          top = triggerRect.bottom + gap;
          left = triggerRect.left + (triggerRect.width - tooltipWidth) / 2;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height - tooltipHeight) / 2;
          left = triggerRect.left - tooltipWidth - gap;
          break;
        case 'right':
          top = triggerRect.top + (triggerRect.height - tooltipHeight) / 2;
          left = triggerRect.right + gap;
          break;
      }

      // Clamp to viewport
      const padding = 10;
      if (left < padding) left = padding;
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltipWidth - padding;
      }
      if (top < padding) top = padding;
      if (top + tooltipHeight > window.innerHeight - padding) {
        top = window.innerHeight - tooltipHeight - padding;
      }

      setTooltipPosition({ top, left });
    });

    return () => cancelAnimationFrame(frameId);
  }, [isVisible, placement, arrow]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getArrowStyles = (): React.CSSProperties => {
    const arrowSize = 8;
    const baseStyles: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    };

    switch (placement) {
      case 'top':
        return {
          ...baseStyles,
          bottom: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
          borderColor: `${backgroundColor} transparent transparent transparent`,
        };
      case 'bottom':
        return {
          ...baseStyles,
          top: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
          borderColor: `transparent transparent ${backgroundColor} transparent`,
        };
      case 'left':
        return {
          ...baseStyles,
          right: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
          borderColor: `transparent transparent transparent ${backgroundColor}`,
        };
      case 'right':
        return {
          ...baseStyles,
          left: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
          borderColor: `transparent ${backgroundColor} transparent transparent`,
        };
      default:
        return baseStyles;
    }
  };

  const triggerElement = (
    <Box
      ref={triggerRef}
      component="span"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        ...wrapperSx,
      }}
    >
      {children}
    </Box>
  );

  // Portalled to document.body so no parent overflow/transform can affect fixed positioning.
  // Rendered off-screen (opacity 0, top/left -9999) during the measuring rAF, then snapped.
  const tooltipPortal = isVisible ? createPortal(
    <Box
      ref={tooltipRef}
      sx={{
        position: 'fixed',
        top: tooltipPosition ? `${tooltipPosition.top}px` : '-9999px',
        left: tooltipPosition ? `${tooltipPosition.left}px` : '-9999px',
        zIndex: 10000,
        pointerEvents: 'none',
        opacity: tooltipPosition ? 1 : 0,
        animation: tooltipPosition ? 'fadeIn 0.15s ease-in-out' : 'none',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'scale(0.95)' },
          to: { opacity: 1, transform: 'scale(1)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor,
          color: textColor,
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: 1.5,
          width: typeof width === 'number' ? `${width}px` : width,
          maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
          wordWrap: 'break-word',
          boxShadow: '0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 12px 16px -4px rgba(10, 13, 18, 0.08)',
        }}
      >
        {displayMessage}
        {arrow && <Box sx={getArrowStyles()} />}
      </Box>
    </Box>,
    document.body
  ) : null;

  // Wrap with ClickAwayListener only for click mode
  if (trigger === 'click') {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box component="span" sx={{ display: 'inline-block' }}>
          {triggerElement}
          {tooltipPortal}
        </Box>
      </ClickAwayListener>
    );
  }

  return (
    <>
      {triggerElement}
      {tooltipPortal}
    </>
  );
};

export default Tooltip;
